import { parseAgrTicker, toRawTicker } from "../domain/agr.helpers";

interface ReleaseValue {
  duration?: number | null;
  chronoUnit?: string;
}

interface TimeLimit {
  timeLimit: string;
  timeZone: string;
}

interface PolicyConfiguration {
  type: string;
  penalty: number | string;
  cancelOnline: boolean;
  minRelease?: ReleaseValue | number;
  minReleaseTimeLimit?: TimeLimit;
  maxRelease?: ReleaseValue | number;
  maxReleaseTimeLimit?: TimeLimit;
}

interface NormalizedConfiguration {
  type: string;
  penalty: number;
  cancelOnline: boolean;
  minRelease?: ReleaseValue;
  minReleaseTimeLimit?: TimeLimit;
  maxRelease?: ReleaseValue;
  maxReleaseTimeLimit?: TimeLimit;
}

interface PolicyEntity {
  ticker: string;
  propertyTicker?: string;
  validFrom?: string;
  validTo?: string;
  ordering?: number | string;
  active?: boolean;
  enableAutomaticCharge?: boolean;
  penaltyDeposit?: boolean;
  configurations?: PolicyConfiguration[];
  translations?: Record<string, unknown>[];
}

interface ChainBodyEntry {
  ticker: string;
  propertyTicker: string;
  parentPropertyTicker?: string;
  parentTicker?: string;
  validFrom?: string;
  validTo?: string;
  ordering: number;
  active?: boolean;
  enableAutomaticCharge?: boolean;
  penaltyDeposit?: boolean;
  configurations: NormalizedConfiguration[];
  translations?: Record<string, unknown>[];
}

interface BuildChainBodyResult {
  chainBody: Record<string, ChainBodyEntry>;
  parentTicker: string;
}

function isReleaseCreated(
  release: ReleaseValue | number | undefined | null
): ReleaseValue | undefined {
  if (release == null) return undefined;
  if (typeof release === "number") return { duration: release, chronoUnit: "DAYS" };
  return release.duration === undefined ? undefined : release;
}

export function normalizeConfigurations(
  configurations: PolicyConfiguration[]
): NormalizedConfiguration[] {
  return configurations.map(value => {
    const penalty = parseFloat(String(value.penalty));
    const minRelease = isReleaseCreated(value.minRelease);
    const maxRelease = isReleaseCreated(value.maxRelease);

    const result: NormalizedConfiguration = {
      type: value.type,
      penalty,
      cancelOnline: value.cancelOnline,
    };

    if (minRelease && minRelease.duration !== undefined && minRelease.duration !== null) {
      result.minRelease = minRelease;
    }
    if (value.minReleaseTimeLimit) {
      result.minReleaseTimeLimit = value.minReleaseTimeLimit;
    }
    if (maxRelease && maxRelease.duration !== undefined && maxRelease.duration !== null) {
      result.maxRelease = maxRelease;
    }
    if (value.maxReleaseTimeLimit) {
      result.maxReleaseTimeLimit = value.maxReleaseTimeLimit;
    }

    return result;
  });
}

export function buildPolicyChainBody(
  entity: PolicyEntity,
  propertyTicker: string,
  motors: string[]
): BuildChainBodyResult {
  const ticker = parseAgrTicker(entity.ticker);
  const parentTicker = toRawTicker({ base: ticker.base, kind: "parent" });
  const childTicker = toRawTicker({ base: ticker.base, kind: "child" });

  const policyBase: Omit<ChainBodyEntry, "propertyTicker"> = {
    ticker: childTicker,
    validFrom: entity.validFrom || undefined,
    validTo: entity.validTo || undefined,
    ordering: parseInt(String(entity.ordering), 10) || 0,
    active: entity.active,
    enableAutomaticCharge: entity.enableAutomaticCharge,
    penaltyDeposit: entity.penaltyDeposit,
    configurations: normalizeConfigurations(entity.configurations || []),
    translations: entity.translations,
  };

  const chainBody: Record<string, ChainBodyEntry> = {
    [propertyTicker]: { ...policyBase, propertyTicker, ticker: parentTicker },
  };

  motors.forEach(motorTicker => {
    chainBody[motorTicker] = {
      ...policyBase,
      propertyTicker: motorTicker,
      parentPropertyTicker: propertyTicker,
      parentTicker,
    };
  });

  return { chainBody, parentTicker };
}

export function buildAgrReadonlyLink(
  parentPropertyTicker: string,
  parentTicker: string,
  entityName: string
): string {
  return `/v6/ng/${parentPropertyTicker}/${entityName}/${parentTicker}`;
}
